import Vue, { CreateElement } from '@src/vue'
import { Component, Prop, Watch } from 'vue-property-decorator'
import draggable from 'vuedraggable'

import { ExerciseResponse } from '@src/data/exercise-response'
import { ExerciseFilters } from '@src/state/store'
import { WorkoutExercise } from '@src/data/workout-exercise'
import { ExerciseService } from '@src/service/exercise'
import { countItemAppearance } from '@src/util/functions'
import { Link } from '@src/elements/link/link'
import { Loader } from '@src/components/loading-screen/loader/loader'
import { ExerciseCardSelect } from '@src/components/card/exercise-card/exercise-card-select/exercise-card-select'
import ExerciseTagsFilters from '@src/routes/content/components/exercise-tags-filters/exercise-tags-filters'
import { BOTTOM_SCROLL_THRESHOLD, CARDS_ON_BUILDER_PAGE, DRAG_COMMON_SETTING } from '@src/util/constants'
import { ErrorScreen } from '@src/components/error-screen/error-screen'
import { defaultExerciseFilters } from '@src/state/dafaults'
import { Checkbox } from '@src/elements/input/checkbox'
import { DesktopOnly } from '@src/components/desktop-only/desktop-only'
import '@src/components/exercise-selector/exercise-selector.sass'

@Component({
  components: {
    draggable: draggable
  },
  computed: {
    availableExercises: {
      /**
       * Filter the exercises by the tags and name. One exercise can have multiple tags. The filters also represent multiple tags.
       */
      get: function () {
        const state = this.$store.state
        // filter
        let filteredExercises = this.$store.state.availableExercises.filter((exercise) =>
          ExerciseService.filterByTagsAndEquipment(exercise, state.exerciseFilters.tags || new Set([]))
          // the number is only checked for a name that did not match, so the name is never empty there
          && (ExerciseService.filterByName(exercise, state.exerciseFilters.name || '') || ExerciseService.filterByNumber(exercise, state.exerciseFilters.name || /* istanbul ignore next */ ''))
          && ExerciseService.filterByFavorites(exercise, state.exerciseFilters.favoritesOnly || false, state.favoriteExerciseIds)
        )
        // sort
        if (!state.exerciseFilters.name || !state.exerciseFilters.name.trim().length) {
          return filteredExercises
        } else if (isNaN(Number(state.exerciseFilters.name))) {
          return filteredExercises.sort((a, b) => a.getName() > b.getName() ? 1 : a.getName() < b.getName() ? -1 : 0)
        } else {
          return filteredExercises.sort((a, b) => a.number > b.number ? 1 : a.number < b.number ? -1 : 0)
        }
      },
      set: function () {
        // draggable needs a setter but the availableExercises should never be set by draggable
        // we don't want to change any order in the selector list (drag and drop in selector list)
        // when filters are applied this will be visible in the getter
      }
    },
    exerciseFilters: {
      get: function () {
        return this.$store.state.exerciseFilters || {}
      },
      set: function (filters) {
        this.$store.state.exerciseFilters = filters
      }
    },
    loadAvailableExercisesComplete () {
      return this.$store.state.loadAvailableExercisesComplete
    }
  }
})
export default class ExerciseSelector extends Vue {

  @Prop({ required: false })
  selectedExercises: WorkoutExercise[]

  @Prop({ required: false, default: false })
  selectCard: boolean

  @Prop({ required: false, default: false })
  protected expandableFilters: boolean

  private exerciseFilters: ExerciseFilters
  private dragOptions: any = {
    group: {
      name: 'exerciseSelector', // for example if there are two lists: A and B:
      pull: 'clone', // pull: 'clone' - is used to copy the selected element from A and move it in B;
      put: false // put: true - if drag&drop cloned element fom B in A
    },
    ...DRAG_COMMON_SETTING,
    sort: false,
    chosenClass: 'dragged-exercise'
  }
  private availableExercises: ExerciseResponse[]
  private loadAvailableExercisesComplete: boolean

  // needed for pagination
  private renderedExerciseCards: ExerciseResponse[] = []
  private page: number = 0

  private hidePreview = false
  private hideDetails = false

  constructor () {
    super()
  }

  created () {
    this.dragOptions.disabled = this.selectCard
  }

  mounted () {
    this.renderedExerciseCards = this.availableExercises.slice(this.page * CARDS_ON_BUILDER_PAGE, (this.page + 1) * CARDS_ON_BUILDER_PAGE)
    this.scroll()
  }

  clearFilters () {
    this.$store.dispatch('filterExercises', defaultExerciseFilters())
  }

  beforeDestroy () {
    this.$store.dispatch('addSingleExerciseToWorkout', { exercise: null })
    this.clearFilters()
  }

  @Watch('exerciseFilters')
  goToBeginning () {
    this.page = 0
    this.renderedExerciseCards = this.availableExercises.slice(this.page * CARDS_ON_BUILDER_PAGE, (this.page + 1) * CARDS_ON_BUILDER_PAGE)
    document.getElementById('exercises-selector')!.scrollTop = 0
  }

  scroll () {
    document.getElementById('exercises-selector')!.addEventListener('scroll', this.checkBottomOfPage)
  }

  checkBottomOfPage () {
    let bottomOfWindow = document.getElementById('exercises-selector')!.scrollTop
      + document.getElementById('exercises-selector')!.offsetHeight
      + BOTTOM_SCROLL_THRESHOLD
      >= document.getElementById('exercises-selector')!.scrollHeight

    // if bottom of loaded page, load more
    if (bottomOfWindow) {
      if (this.availableExercises.length > this.renderedExerciseCards.length) {
        this.page++
        this.renderedExerciseCards = this.renderedExerciseCards.concat(this.availableExercises.slice(this.page * CARDS_ON_BUILDER_PAGE, (this.page + 1) * CARDS_ON_BUILDER_PAGE))
      }
    }
  }

  render (h: CreateElement) {

    const exerciseCards = this.renderedExerciseCards.map(exercise => {
      return <ExerciseCardSelect key={exercise.id}
                                 exercise={exercise}
                                 type={this.selectCard ? 'select' : 'add'}
                                 selectedFilters={this.exerciseFilters.tags || {}}
                                 exerciseCounter={countItemAppearance(this.selectedExercises, exercise.id)}
                                 hidePreview={this.hidePreview}
                                 hideDetails={this.hideDetails}/>
    })
    const isStillLoading = !this.loadAvailableExercisesComplete ||
      (this.availableExercises.length === 0 && !this.tagFiltersSelected() && !this.exerciseFilters.name && !this.exerciseFilters.favoritesOnly)

    return <div class='selector'>
      <ExerciseTagsFilters expandable={this.expandableFilters} />
      <DesktopOnly>
        <div class='hide-options'>
          <Checkbox value={this.hidePreview} change={() => this.hidePreview = !this.hidePreview} text={this.$t('PAGE.CONTENT.WORKOUT.HIDE_PREVIEW')} />
          <Checkbox value={this.hideDetails} change={() => this.hideDetails = !this.hideDetails} text={this.$t('PAGE.CONTENT.WORKOUT.HIDE_DETAILS')} />
        </div>
      </DesktopOnly>

      <div class='exercises' id='exercises-selector'>
        {isStillLoading ?
          <Loader />

          : exerciseCards.length ?
            <draggable v-model={this.availableExercises} options={this.dragOptions}>
              {exerciseCards}
            </draggable>

            : this.renderErrorScreen(h)
        }
      </div>
    </div>
  }
  renderErrorScreen (h: CreateElement) {
    let message: any = ''
    if (this.exerciseFilters.name) message = this.$t('PAGE.CONTENT.WORKOUTS.NOT_FOUND.FILTER.NAME', { query: this.exerciseFilters.name })
    else if (this.tagFiltersSelected() || this.exerciseFilters.favoritesOnly) message = this.$t('PAGE.CONTENT.WORKOUT.NO_EXERCISES_FILTERS')
    return <ErrorScreen message={message}>
      <Link variant='colored' align='center' click={this.clearFilters} label={this.$t('PAGE.CONTENT.WORKOUT.SHOW_ALL_EXERCISES')} />
    </ErrorScreen>
  }

  tagFiltersSelected () {
    for (let type in this.exerciseFilters.tags!) {
      if (this.exerciseFilters.tags![type].size > 0) return true
    }
    return false
  }
}
